/**
 * Per-project sync badge + button, shown on a project card.
 *
 * NOTE: strings are plain English for the demo. TODO: route through i18n
 * (add a cloud-sync partial across all 9 languages) before shipping widely.
 */

import { useEffect } from 'react'
import { toast } from 'sonner'
import { Cloud, CloudUpload, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

import { useCloudSyncStore } from '../stores/cloud-sync-store'

interface ProjectSyncControlProps {
  projectId: string
  /** The LOCAL project's last-modified time, to compare against the cloud's. */
  updatedAt: number
}

type SyncStatus = 'syncing' | 'not-synced' | 'synced' | 'modified'

export function ProjectSyncControl({ projectId, updatedAt }: ProjectSyncControlProps) {
  const configured = useCloudSyncStore((s) => s.configured)
  const cloudMeta = useCloudSyncStore((s) => s.cloudProjects.get(projectId))
  const isSyncing = useCloudSyncStore((s) => s.syncing.has(projectId))
  const ensureLoaded = useCloudSyncStore((s) => s.ensureLoaded)
  const syncProject = useCloudSyncStore((s) => s.syncProject)

  // Load the cloud list once (the store dedupes concurrent/extra calls).
  useEffect(() => {
    void ensureLoaded()
  }, [ensureLoaded])

  // Hidden entirely when cloud sync isn't configured.
  if (!configured) return null

  const status: SyncStatus = isSyncing
    ? 'syncing'
    : !cloudMeta
      ? 'not-synced'
      : cloudMeta.updatedAt >= updatedAt
        ? 'synced'
        : 'modified'

  const handleSync = async (e: React.MouseEvent) => {
    // Don't let the click bubble to the card (which would select/open it).
    e.preventDefault()
    e.stopPropagation()
    try {
      await syncProject(projectId)
      toast.success('Project synced to cloud')
    } catch (error) {
      toast.error('Sync failed', {
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  return (
    <div
      className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-border"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <SyncBadge status={status} />
      {status !== 'synced' && (
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5"
          disabled={isSyncing}
          onClick={handleSync}
        >
          {isSyncing ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <CloudUpload className="w-3.5 h-3.5" />
          )}
          {status === 'modified' ? 'Sync changes' : 'Sync'}
        </Button>
      )}
    </div>
  )
}

function SyncBadge({ status }: { status: SyncStatus }) {
  if (status === 'syncing') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Syncing…
      </span>
    )
  }
  if (status === 'synced') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-green-600 dark:text-green-500">
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
        Synced
      </span>
    )
  }
  if (status === 'modified') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
        <CloudUpload className="w-3.5 h-3.5" />
        Changes not synced
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Cloud className="w-3.5 h-3.5" />
      Not synced
    </span>
  )
}
